import { PosthogFeatureFlags, type PosthogFeatureFlagKey } from '@/lib/posthog'
import { usePwaStore } from '@/store/pwa'
import type { IIcons } from '@/views/components/ui/icons/types'

const pwaAppName = import.meta.env.VITE_PWA_APP_NAME || 'EverGPT'

/**
 * Primary CTA: `label` is always shown, and `onClick` runs before the host
 * finalizes (session dismiss + dequeue). There is no link form — every
 * notification left acts on the app itself.
 */
export interface InAppNotificationAction {
  label: string
  onClick: () => void | Promise<void>
}

export interface InAppNotificationConfig {
  id: string
  title: string
  description: string
  headerIcon: IIcons
  /** When omitted, no primary button is shown (close only). */
  actionButton?: InAppNotificationAction
  posthogFeatureFlag?: PosthogFeatureFlagKey
  isEligible?: () => boolean
  /**
   * When true, close (×) does not write session dismiss — use `onDismiss` for snooze/state instead.
   */
  skipSessionDismissOnClose?: boolean
  /**
   * When true, primary CTA does not session-dismiss (e.g. reload should re-prompt).
   */
  skipSessionDismissOnAction?: boolean
  /** Replaces default close behavior (session dismiss). */
  onDismiss?: () => void
}

/**
 * The PWA prompts, and only those. This surface once also carried editorial
 * notices (a satisfaction survey, a personalisation nudge); those are gone, and
 * broadcast messaging is the announcements feature's job now — see
 * `views/components/modals/ModalAnnouncement`.
 *
 * Both entries here run before `/me`, so eligibility never reads the user.
 */
export function getInAppNotificationConfigs(): InAppNotificationConfig[] {
  const pwa = usePwaStore()

  return [
    {
      id: 'pwa-update',
      title: 'A new version is ready.',
      description: 'Reload to get the latest fixes and improvements.',
      headerIcon: 'refresh',
      skipSessionDismissOnClose: true,
      skipSessionDismissOnAction: true,
      // No `pwa` flag gate: this prompt now also comes from the build-version watch
      // (see appVersionWatch.ts), which runs for every user. Keeping the gate would
      // hide "your app is out of date" from precisely the users who have no service
      // worker to fix it for them.
      isEligible: () => pwa.showUpdateNotification,
      onDismiss: () => pwa.dismissUpdatePrompt(),
      actionButton: {
        label: 'Reload',
        onClick: () => pwa.applyUpdate(),
      },
    },
    {
      id: 'pwa-install',
      title: `Install ${pwaAppName}`,
      description: 'Add the app to your desktop for quick access.',
      headerIcon: 'download',
      skipSessionDismissOnClose: true,
      skipSessionDismissOnAction: true,
      posthogFeatureFlag: PosthogFeatureFlags.Pwa,
      isEligible: () => pwa.showInstallNotification,
      onDismiss: () => pwa.snoozeInstallOffer(),
      actionButton: {
        label: 'Install',
        onClick: () => pwa.promptInstall(),
      },
    },
  ]
}
